import logger from '../logger';
import { IAssistAnswer, IAssistEnvelope } from './types';

/**
 * HITL 8.0.0: delivers an `assist_answer` to the event API (`POST /v1/event`),
 * the issue report's REST path. No socket: the card has nothing to receive,
 * and the answer must not wait on a connection being opened.
 *
 * The agent has 2–4 s, so a failed post is retried quickly and only twice;
 * past that the answer is too late to count, and it is dropped with a warning.
 * The envelope is built once: every attempt carries the same `answer_id`, so
 * gpt-context counts it once.
 *
 * Each attempt is aborted after `ASSIST_ANSWER_ATTEMPT_TIMEOUT_MS`: a stalled
 * POST would otherwise hang past the card's window, the retries would never
 * run, and the answer would be lost with nothing logged.
 */

/** Delay before attempts 2 and 3. */
export const ASSIST_ANSWER_RETRY_MS = [250, 750];
/** Per-attempt cap: a POST still pending after this is aborted and retried. */
export const ASSIST_ANSWER_ATTEMPT_TIMEOUT_MS = 1500;

export interface IAssistAnswerApi {
  eventAPI: string;
  eventAPIAuth: string;
}

const post = async (
  envelope: IAssistEnvelope<IAssistAnswer>,
  { eventAPI, eventAPIAuth }: IAssistAnswerApi
) => {
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(),
    ASSIST_ANSWER_ATTEMPT_TIMEOUT_MS
  );
  try {
    // Raw fetch, not utils/api.ts: its network-call log would post a second
    // event to this backend for every answer.
    const response = await fetch(`${eventAPI}/v1/event`, {
      method: 'POST',
      headers: {
        Authorization: `Basic ${eventAPIAuth}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(envelope),
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Resolves true once delivered, false when every attempt failed. */
export const deliverAssistAnswer = async (
  envelope: IAssistEnvelope<IAssistAnswer>,
  api: IAssistAnswerApi
): Promise<boolean> => {
  const requestId = envelope.data.request_id;
  if (!api.eventAPI || !api.eventAPIAuth) {
    logger.warn({
      message:
        'Cannot deliver assist answer: EVENT_API or EVENT_API_BASIC_AUTH_PARAM is not configured.',
      requestId,
      isTR: true,
    });
    return false;
  }
  for (
    let attempt = 0;
    attempt <= ASSIST_ANSWER_RETRY_MS.length;
    attempt += 1
  ) {
    if (attempt > 0) {
      // eslint-disable-next-line no-await-in-loop
      await wait(ASSIST_ANSWER_RETRY_MS[attempt - 1]);
    }
    // eslint-disable-next-line no-await-in-loop
    if (await post(envelope, api)) return true;
  }
  logger.warn({
    message: 'Assist answer not delivered',
    requestId,
    answerId: envelope.data.answer_id,
    attempts: ASSIST_ANSWER_RETRY_MS.length + 1,
    isTR: true,
  });
  return false;
};
